import { ElementType, FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { SkeletonLoader, Text } from '@frontend/design-system';
import { getModuleTaskStats } from '../../utils';
import { GlassCard } from '../glass-card';
import { TaskItem } from './task-item';

interface ModuleTasksCardProps {
  module: OnboardingTasksV2.Types.ModuleRow;
  as?: ElementType;
}
export const ModuleTasksCard: FC<ModuleTasksCardProps> = ({ module, as }) => {
  const { t } = useTranslation('onboardingDashboard');

  const { completed, hasTaskStarted, scheduled, total } = useMemo(() => {
    return getModuleTaskStats([module]);
  }, [module]);

  const hasIncompleteTasks = scheduled + completed < total;
  const allTasksCompleted = completed === total;

  const subTitle = allTasksCompleted
    ? t('Completed')
    : hasTaskStarted
    ? t('{{completed}} / {{total}} tasks completed', { completed, total })
    : t('Not started');

  return (
    <GlassCard as={as} containerStyles={hasIncompleteTasks ? styles.incompleteTasks : undefined}>
      <GlassCard.Header
        title={
          <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
            {allTasksCompleted && <Icon name='check-small' color='success' />}
            <Text size='large' weight='bold'>
              {module.moduleName}
            </Text>
          </div>
        }
        subTitle={subTitle}
      />
      {!allTasksCompleted && (
        <GlassCard.Body as='ul' bodyStyles={styles.bodyUlStyles}>
          {module.tasks.map((task) => (
            <TaskItem key={task.id} task={task} />
          ))}
        </GlassCard.Body>
      )}
    </GlassCard>
  );
};

export const ModuleTasksCardLoader: FC<{ as?: ElementType }> = ({ as }) => {
  return (
    <GlassCard as={as}>
      <GlassCard.Header>
        <SkeletonLoader width={'50%'} height={24} />
      </GlassCard.Header>
      <GlassCard.Body as='ul' bodyStyles={styles.bodyUlStyles}>
        {Array.from({ length: 3 }).map((_, index) => (
          <li key={index} css={{ height: 56, padding: theme.spacing(2) }}>
            <SkeletonLoader />
          </li>
        ))}
      </GlassCard.Body>
    </GlassCard>
  );
};

const styles = {
  incompleteTasks: css`
    border: 2px solid ${theme.colors.indigo40};
  `,
  bodyUlStyles: css`
    list-style: none;
    padding: 0;
    margin: 0;
  `,
};
